import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import { fetchInspectionKindDetail } from '#/api/imsMgmt/settings/inspectionKind/fetchDetail';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { formatDateTimeOrDash } from '#/utils/date';
import { inspectionKindDetailQueryKey } from '#/utils/queryKeys';

import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select/FloatingSelect';
import { ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS } from '#/modules/commonComponents/table/entityStatus';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import InspectionConditionItemsEditor from
  '#/modules/imsMgmt/settings/inspectionKind/components/InspectionConditionItemsEditor';
import { getInspectionKindApiErrorMessage } from
  '#/modules/imsMgmt/settings/inspectionKind/helper/apiError';
import { mapInspectionKindToForm } from
  '#/modules/imsMgmt/settings/inspectionKind/helper/mapInspectionKindForm';

function historyNumbers(revisionNo) {
  const current = Number(revisionNo);
  if (!Number.isInteger(current) || current <= 1) {
    return [];
  }
  const numbers = [];
  for (let number = current - 1; number >= 1; number -= 1) {
    numbers.push(number);
  }
  return numbers;
}

function formValuesFromDetail(detail) {
  const values = mapInspectionKindToForm(detail);
  const activeItems = (detail?.items ?? []).filter((item) => item?.status !== 'DEPRECATED');
  if (activeItems.length === 0) {
    values.items = [];
  }
  return values;
}

const InspectionKindDetailPage = () => {
  const { id } = useParams();
  const canEdit = useCan(PERM.INSPECTION_KIND_UPDATE);
  const detailQuery = useQuery({
    queryKey: inspectionKindDetailQueryKey(id),
    queryFn: () => fetchInspectionKindDetail(id),
    enabled: Boolean(id),
  });
  const detail = detailQuery.data?.data;
  const revisions = historyNumbers(detail?.revisionNo);
  const {
    register,
    control,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      code: '',
      name: '',
      status: 'ACTIVE',
      items: [],
    },
  });

  useEffect(() => {
    if (!detail) {
      return;
    }
    reset(formValuesFromDetail(detail));
  }, [detail, reset]);

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">檢測種類詳情</span>
            </h1>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.inspectionKind}>返回列表</Link>
              </Button>
              {canEdit && detail ? (
                <Button type="button" asChild>
                  <Link to={ROUTES.inspectionKindEdit(id)}>編輯</Link>
                </Button>
              ) : null}
            </div>
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getInspectionKindApiErrorMessage(detailQuery.error, '找不到檢測種類')}
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-muted-foreground">
                {`目前第 ${detail?.revisionNo ?? '—'} 版，生效時間 ${
                  formatDateTimeOrDash(detail?.revision?.effectiveFrom)
                }，更新時間 ${formatDateTimeOrDash(detail?.record?.updatedAt)}。`}
              </p>
              <div className="sheet create_edit create_edit_form">
                <div className="create_edit_form__body">
                  <FloatingInput
                    id="code"
                    label="種類代碼"
                    register={register}
                    readOnly
                  />
                  <FloatingInput
                    id="name"
                    label="名稱"
                    register={register}
                    readOnly
                  />
                  <FloatingSelect
                    id="status"
                    label="狀態"
                    options={ENTITY_STATUS_ACTIVE_INACTIVE_OPTIONS}
                    control={control}
                    isDisabled
                  />
                  <InspectionConditionItemsEditor
                    control={control}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    readOnly
                  />
                </div>
              </div>
              {revisions.length ? (
                <div className="mt-4">
                  <h2 className="mb-2 text-sm font-medium">歷史版本</h2>
                  <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                    {revisions.map((revisionNo) => (
                      <li key={revisionNo}>
                        <Link
                          className="text-sm text-primary underline"
                          to={ROUTES.inspectionKindRevision(id, revisionNo)}
                        >
                          {`第 ${revisionNo} 版`}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default InspectionKindDetailPage;
